/**
 * Excel export for report screens: every rendered table on ONE worksheet,
 * styled so the file reads as a report rather than as a dump.
 *
 * Built off the rendered DOM rather than off the API, which is what makes it an
 * export of the SCREEN: the month and cost filters, the column sort, which
 * groups are expanded and which cost heads are included are all already baked
 * into what is on display, and re-deriving any of that from the raw data would
 * be a second implementation to keep in step with the first.
 *
 * ONE worksheet, every table stacked on it in the order they appear, each under
 * its own named strip. The screen is one scroll and the file is meant to read
 * like it - a workbook of a dozen sheet tabs would be a different document from
 * the one the operator was just looking at.
 *
 * `exceljs` is loaded on demand inside `exportTablesAsExcel`, so a library this
 * size never reaches anyone who does not click Export.
 */

/** Colours, kept in one place so the whole workbook reads as one thing. */
const STYLE = {
  /** Sheet title strip. */
  title: { fill: 'FF1F3864', font: 'FFFFFFFF' },
  /** Column header row. */
  header: { fill: 'FF2F5597', font: 'FFFFFFFF' },
  /** A section band inside the table ("SPEND", a channel name). */
  band: { fill: 'FFD9E2F3', font: 'FF1F3864' },
  /** Totals / grand total row. */
  total: { fill: 'FFE7E6E6', font: 'FF000000' },
  border: 'FFBFBFBF',
  zebra: 'FFF7F9FC',
} as const;

/**
 * The text of one cell, as the file should carry it.
 *
 * Anything marked `data-export-skip` is dropped first. That is the small grey
 * note under a figure - "Carries forward until changed", "10 adsets · as of 8
 * Sep" - which helps on screen but here would glue itself to the number and
 * turn a figure Excel could add up into a string it cannot.
 *
 * What remains, if it is more than one block, is joined with a separator rather
 * than run together: the folded channel band stacks a query count over a pure
 * profit, and raw that reads as "623query -₹75,116pure".
 */
const cellText = (cell: Element): string => {
  const copy = cell.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('[data-export-skip]').forEach((node) => node.remove());

  const blocks = copy.querySelectorAll(':scope > span, :scope > div');
  const source =
    blocks.length > 1
      ? [...blocks].map((block) => block.textContent ?? '')
      : [copy.textContent ?? ''];
  return source
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' · ');
};

/** A figure the sheet shows where a formula has no denominator. */
const DASH_ONLY = /^[-–—]$/;

const CURRENCY = /^(-?)₹\s?([\d,]+(?:\.\d+)?)$/;
const PERCENT = /^(-?[\d,]+(?:\.\d+)?)%$/;
const MULTIPLE = /^(-?[\d,]+(?:\.\d+)?)x$/;
const PLAIN_NUMBER = /^-?[\d,]+(?:\.\d+)?$/;

const toNumber = (digits: string): number => Number(digits.replace(/,/g, ''));

interface Figure {
  value: string | number;
  /** Excel number format, when the text was a figure. */
  format?: string;
}

/**
 * A displayed cell as Excel should hold it.
 *
 * Figures go in as real numbers with a format that renders them the way the
 * screen does, so the file both LOOKS like the screen and can be summed, sorted
 * and charted. A number written as the string "₹1,23,456" would look right and
 * be useless. Anything that is not clearly a figure stays text, including the
 * dash that stands for a missing one - 0 would be a different claim.
 */
export const toFigure = (text: string): Figure => {
  if (text === '' || DASH_ONLY.test(text)) return { value: text };

  const currency = CURRENCY.exec(text);
  if (currency) {
    const value = toNumber(currency[2]!) * (currency[1] === '-' ? -1 : 1);
    return { value, format: '₹#,##0' };
  }

  const percent = PERCENT.exec(text);
  if (percent) {
    // Excel's percent format multiplies by 100, so the stored value is the
    // fraction - 20.83% is held as 0.2083 and still reads as 20.83%. Rounded
    // back after the divide, because 20.83 / 100 lands on 0.20829999999999999
    // in binary floating point and that is what the formula bar would show.
    return { value: Number((toNumber(percent[1]!) / 100).toFixed(10)), format: '0.00%' };
  }

  const multiple = MULTIPLE.exec(text);
  if (multiple) return { value: toNumber(multiple[1]!), format: '0.00"x"' };

  if (PLAIN_NUMBER.test(text)) return { value: toNumber(text), format: '#,##0.##' };

  return { value: text };
};

/** What a row is, which decides how it is painted. */
type RowKind = 'header' | 'band' | 'total' | 'body';

const rowKind = (tr: HTMLTableRowElement, cells: Element[]): RowKind => {
  if (tr.closest('thead')) return 'header';
  // A band spans the table on one label, e.g. "SPEND" or a channel name.
  const spans = cells.some((cell) => Number(cell.getAttribute('colspan') ?? '1') > 1);
  const filled = cells.filter((cell) => cellText(cell) !== '').length;
  if (spans && filled <= 2) return 'band';
  const first = cells[0] ? cellText(cells[0]) : '';
  if (/^(grand )?total$/i.test(first)) return 'total';
  return 'body';
};

/** Excel forbids : \ / ? * [ ] in a sheet name, and caps it at 31 characters. */
const sheetName = (title: string, taken: Set<string>): string => {
  const base =
    title
      .replace(/[:\\/?*[\]]/g, ' ')
      .trim()
      .slice(0, 31) || 'Sheet';
  if (!taken.has(base)) {
    taken.add(base);
    return base;
  }
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base.slice(0, 28)} ${suffix}`;
    if (!taken.has(candidate)) {
      taken.add(candidate);
      return candidate;
    }
  }
};

/**
 * Waits for the tables on screen to stop loading, up to `timeoutMs`.
 *
 * Forcing the deferred blocks to render starts their reads; a file written one
 * frame later would be a file of shimmer placeholders. Skeletons are found by
 * their pulse class, which is how every report renders one, and the wait gives
 * up rather than hanging - a partial export beats a button that never responds.
 */
const waitForTablesReady = async (container: HTMLElement, timeoutMs = 10_000): Promise<void> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (!container.querySelector('.animate-pulse')) return;
    await new Promise((resolve) => setTimeout(resolve, 120));
  }
};

/**
 * Writes every table in `container` to a styled .xlsx and downloads it.
 *
 * Returns false when there is nothing to export, so the caller can say so
 * rather than handing over an empty file.
 */
export const exportTablesAsExcel = async (
  container: HTMLElement,
  filename: string,
  workbookTitle = 'Report',
): Promise<boolean> => {
  await waitForTablesReady(container);

  const tables = [...container.querySelectorAll('table')];
  if (tables.length === 0) return false;

  const ExcelJS = await import('exceljs');
  const book = new ExcelJS.Workbook();
  book.creator = 'Utazzo CRM';
  book.created = new Date();

  // No frozen pane: this sheet stacks a dozen tables, so there is no one header
  // row to pin, and freezing the label column alone makes Excel draw that column
  // twice side by side as soon as the sheet is scrolled sideways.
  const sheet = book.addWorksheet(sheetName(workbookTitle, new Set()));

  const widest: number[] = [];

  tables.forEach((table, index) => {
    const title =
      table.closest('[data-report-title]')?.getAttribute('data-report-title') ??
      `${workbookTitle} ${index + 1}`;

    // A named strip above each table, so the one long sheet still says where
    // one report ends and the next begins.
    const titleRow = sheet.addRow([title]);
    titleRow.height = 22;
    titleRow.getCell(1).font = { bold: true, size: 13, color: { argb: STYLE.title.font } };
    titleRow.getCell(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: STYLE.title.fill },
    };
    titleRow.getCell(1).alignment = { vertical: 'middle' };

    [...table.querySelectorAll('tr')].forEach((tr) => {
      const cells = [...tr.querySelectorAll('th, td')];
      const kind = rowKind(tr as HTMLTableRowElement, cells);

      const values: Array<string | number> = [];
      const formats: Array<string | undefined> = [];
      cells.forEach((cell) => {
        const figure = toFigure(cellText(cell));
        values.push(figure.value);
        formats.push(figure.format);
        // Pad a grouped header's colSpan so the columns under it stay lined up.
        const span = Number(cell.getAttribute('colspan') ?? '1');
        for (let extra = 1; extra < span; extra += 1) {
          values.push('');
          formats.push(undefined);
        }
      });

      const row = sheet.addRow(values);

      row.eachCell({ includeEmpty: true }, (cell, column) => {
        const format = formats[column - 1];
        if (format) cell.numFmt = format;
        cell.alignment = {
          vertical: 'middle',
          horizontal: column === 1 ? 'left' : 'right',
          wrapText: column === 1,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: STYLE.border } },
          left: { style: 'thin', color: { argb: STYLE.border } },
          bottom: { style: 'thin', color: { argb: STYLE.border } },
          right: { style: 'thin', color: { argb: STYLE.border } },
        };

        if (kind === 'header') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: STYLE.header.fill } };
          cell.font = { bold: true, color: { argb: STYLE.header.font } };
        } else if (kind === 'band') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: STYLE.band.fill } };
          cell.font = { bold: true, color: { argb: STYLE.band.font } };
        } else if (kind === 'total') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: STYLE.total.fill } };
          cell.font = { bold: true };
        } else if (row.number % 2 === 1) {
          // Zebra striping, so a wide row is followed across the sheet.
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: STYLE.zebra } };
        }

        const text = String(cell.value ?? '');
        widest[column - 1] = Math.max(widest[column - 1] ?? 10, Math.min(text.length + 3, 46));
      });
    });

    // A blank line between tables, as the screen puts a gap between them.
    sheet.addRow([]);
  });

  sheet.columns.forEach((column, index) => {
    column.width = widest[index] ?? 12;
  });

  const buffer = await book.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
  return true;
};
